/**
 * CampaignTagControl — show / set the one Static Tag assigned to a campaign.
 * Reused on the Scheduler row and the campaign page. Display value is supplied
 * by the caller (`tag`); editing goes through useSetCampaignTag.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo, useState, type FC } from 'react';
import { Tag, X } from 'lucide-react';

import { Button, SearchableSelect } from '@/components/ui';
import { useStaticTags } from '@/hooks';
import { useToast } from '@/providers/ToastProvider';
import { getApiErrorMessage } from '@/lib/utils';
import { StaticTagBadge } from '@/features/smswiser-campaigns/components/drop-modals/sections/StaticTagBadge';
import type { CampaignStaticTag } from '@/features/flow-optimizer/services/campaign-tags-api';
import type { CampaignTagProvider } from '@/features/campaigns/tracker/campaign-tracker-access';
import { useSetCampaignTag } from '@/features/flow-optimizer/hooks/useCampaignStaticTag';
import { tagSelectOptions } from '@/features/flow-optimizer/components/tag-select-options';

const NONE = '';

export interface CampaignTagControlProps {
  provider: CampaignTagProvider;
  campaignId: string;
  tag: CampaignStaticTag | null | undefined;
  canEdit: boolean;
  emptyLabel?: string;
}

export const CampaignTagControl: FC<CampaignTagControlProps> = ({
  provider,
  campaignId,
  tag,
  canEdit,
  emptyLabel = '+ Tag',
}) => {
  const [editing, setEditing] = useState(false);
  const toast = useToast();
  const save = useSetCampaignTag(provider, campaignId);

  const apply = (next: string) =>
    save.mutate(next === NONE ? null : next, {
      onSuccess: () => {
        toast.success('Tag updated');
        setEditing(false);
      },
      onError: (err) => toast.error('Failed to update tag', getApiErrorMessage(err)),
    });

  if (editing) {
    return <TagEditor value={tag?.id ?? NONE} onApply={apply} onCancel={() => setEditing(false)} />;
  }

  if (!tag) {
    if (!canEdit) return <span className="text-muted-foreground">—</span>;
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground"
        onClick={() => setEditing(true)}
      >
        <Tag className="h-3 w-3" aria-hidden /> {emptyLabel}
      </Button>
    );
  }

  return (
    <button
      type="button"
      disabled={!canEdit}
      onClick={() => canEdit && setEditing(true)}
      className="inline-flex items-center rounded-md transition-opacity enabled:hover:opacity-80 disabled:cursor-default"
      aria-label={canEdit ? `Change tag (${tag.name})` : tag.name}
    >
      <StaticTagBadge name={tag.name} color={tag.color} />
    </button>
  );
};

interface TagEditorProps {
  value: string;
  onApply: (next: string) => void;
  onCancel: () => void;
}

const TagEditor: FC<TagEditorProps> = ({ value, onApply, onCancel }) => {
  const { data: tags } = useStaticTags();
  const options = useMemo(
    () => tagSelectOptions(tags ?? [], [{ value: NONE, label: 'No tag' }]),
    [tags],
  );
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-40">
        <SearchableSelect options={options} value={value} onChange={onApply} placeholder="Select tag…" />
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        aria-label="Cancel tag edit"
        onClick={onCancel}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </span>
  );
};
